import type { Budget } from '../types/budget';
import type { Category } from '../types/category';
import { parseDate, getWeekStartDate, formatDate } from './dateUtils';
import type { CategoryType } from '../types/category';
import { getCategoryType } from './categoryMapper';

/**
 * Represents an expected transaction derived from a budget
 */
export interface BudgetExpectedTransaction {
  date: string; // Date in YYYY-MM-DD format
  amount: number;
  category_id: number | null;
  categoryType: CategoryType;
  payee: string;
  source: 'budget';
  budget_id: number; // category_id of the budget
}

/**
 * Convert budgets into expected transactions for cash flow projections
 * Supports budgets with different granularities (weekly, monthly, yearly):
 * - Weekly budgets: amount is used directly per week
 * - Monthly budgets: amount is distributed evenly across weeks in that month
 * - Yearly budgets: amount is distributed evenly across weeks in that year
 * @param budgets Array of budgets from Lunch Money
 * @param categories Array of categories for type mapping
 * @param startDate Start date of the planning period
 * @param endDate End date of the planning period
 * @param weekDates Optional array of actual week dates (Sunday dates) from the planner. If provided, budget transactions will only be created for these exact dates.
 */
export function deriveExpectedTransactionsFromBudgets(
  budgets: Budget[],
  categories: Category[],
  startDate: Date,
  endDate: Date,
  weekDates?: string[]
): BudgetExpectedTransaction[] {
  const categoryTypeMap = new Map<number, CategoryType>();
  categories.forEach(cat => {
    categoryTypeMap.set(cat.id, getCategoryType(cat));
  });

  const expectedTransactions: BudgetExpectedTransaction[] = [];
  
  budgets.forEach(budget => {
    try {
      // Skip budgets that are excluded
      if (budget.exclude_from_budget) {
        return;
      }

      const categoryType = categoryTypeMap.get(budget.category_id) || null;
      
      // Get granularity from config, default to 'monthly' for backward compatibility
      const granularity = budget.config?.granularity || 'monthly';
      
      // Process each period's budget data
      if (!budget.data || typeof budget.data !== 'object') {
        console.warn(`[Budget] Budget ${budget.category_name} has invalid data`);
        return;
      }
      
      Object.entries(budget.data).forEach(([periodDateStr, budgetData]) => {
        try {
          if (!budgetData || typeof budgetData !== 'object') {
            console.error(`[Budget] Invalid budgetData for ${periodDateStr} in ${budget.category_name}`);
            return;
          }
          
          // Only process budgets that have an amount set (skip null, undefined, and 0)
          // Note: 0 means no budget, so we skip it
          if (budgetData.budget_to_base === null || budgetData.budget_to_base === undefined || budgetData.budget_to_base === 0) {
            return;
          }

          // Parse period start date based on granularity
          let periodStart: Date;
          
          if (granularity === 'weekly') {
            // Weekly: date key is week start (Sunday) in YYYY-MM-DD format
            if (!periodDateStr.match(/^\d{4}-\d{2}-\d{2}$/)) {
              console.error(`[Budget] Invalid date format for weekly budget: ${periodDateStr}`);
              return;
            }
            periodStart = parseDate(periodDateStr);
            // Ensure it's a Sunday (week start)
            const dayOfWeek = periodStart.getDay();
            if (dayOfWeek !== 0) {
              // Adjust to Sunday of that week
              periodStart = getWeekStartDate(periodStart);
            }
          } else if (granularity === 'monthly') {
            // Monthly: date key is first day of month
            if (periodDateStr.match(/^\d{4}-\d{2}$/)) {
              // YYYY-MM format - parse as first day of month
              periodStart = parseDate(periodDateStr + '-01');
            } else if (periodDateStr.match(/^\d{4}-\d{2}-\d{2}$/)) {
              // YYYY-MM-DD format - parse directly
              periodStart = parseDate(periodDateStr);
              // If it's not the first day of the month, adjust to first day
              if (periodStart.getDate() !== 1) {
                periodStart = new Date(periodStart.getFullYear(), periodStart.getMonth(), 1);
              }
            } else {
              console.error(`[Budget] Invalid date format: ${periodDateStr}`);
              return;
            }
          } else if (granularity === 'yearly') {
            // Yearly: date key is first day of year
            if (periodDateStr.match(/^\d{4}$/)) {
              // YYYY format - parse as first day of year
              periodStart = parseDate(periodDateStr + '-01-01');
            } else if (periodDateStr.match(/^\d{4}-\d{2}-\d{2}$/)) {
              // YYYY-MM-DD format - parse and adjust to first day of year
              periodStart = parseDate(periodDateStr);
              periodStart = new Date(periodStart.getFullYear(), 0, 1);
            } else {
              console.error(`[Budget] Invalid date format for yearly budget: ${periodDateStr}`);
              return;
            }
          } else {
            console.error(`[Budget] Unsupported granularity: ${granularity}`);
            return;
          }
          
          if (isNaN(periodStart.getTime())) {
            console.error(`[Budget] Invalid date parsed from ${periodDateStr}`);
            return;
          }
          
          // Calculate period end based on granularity
          let periodEnd: Date;
          if (granularity === 'weekly') {
            // Week ends on Saturday (6 days after Sunday)
            periodEnd = new Date(periodStart);
            periodEnd.setDate(periodEnd.getDate() + 6);
          } else if (granularity === 'monthly') {
            // Month ends on last day of month
            periodEnd = new Date(periodStart.getFullYear(), periodStart.getMonth() + 1, 0);
          } else { // yearly
            // Year ends on December 31
            periodEnd = new Date(periodStart.getFullYear(), 11, 31);
          }
          
          // Check if this period overlaps with our date range
          if (periodEnd < startDate || periodStart > endDate) {
            return;
          }

          // Calculate how many weeks this period spans in our date range
          const effectiveStart = periodStart < startDate ? startDate : periodStart;
          const effectiveEnd = periodEnd > endDate ? endDate : periodEnd;
          
          // Get all weeks that fall within this period
          // If weekDates are provided, use only those exact dates (matching planner weeks)
          // Otherwise, calculate weeks independently
          const weeks: Date[] = [];
          
          if (weekDates && weekDates.length > 0) {
            // Use the exact week dates from the planner
            weekDates.forEach(weekDateStr => {
              try {
                const weekDate = parseDate(weekDateStr);
                if (isNaN(weekDate.getTime())) {
                  console.error(`[Budget] Invalid week date: ${weekDateStr}`);
                  return;
                }
                // Check if this week overlaps with the period
                const weekEnd = new Date(weekDate);
                weekEnd.setDate(weekEnd.getDate() + 6);
                
                if (weekDate <= effectiveEnd && weekEnd >= effectiveStart) {
                  weeks.push(weekDate);
                }
              } catch (error) {
                console.error(`[Budget] Error processing week date ${weekDateStr}:`, error);
              }
            });
          } else {
            // Fallback: calculate weeks independently
            let currentWeekStart = getWeekStartDate(effectiveStart);
            
            while (currentWeekStart <= effectiveEnd) {
              // Check if this week overlaps with the period
              const weekEnd = new Date(currentWeekStart);
              weekEnd.setDate(weekEnd.getDate() + 6);
              
              if (currentWeekStart <= effectiveEnd && weekEnd >= effectiveStart) {
                weeks.push(new Date(currentWeekStart));
              }
              
              currentWeekStart = new Date(currentWeekStart);
              currentWeekStart.setDate(currentWeekStart.getDate() + 7);
            }
          }

          if (weeks.length === 0) {
            return;
          }

          // Calculate weekly amount based on granularity
          // Budget amounts are stored as positive values in Lunch Money
          // We'll use the absolute value and let the calculation logic handle the sign based on category type
          let weeklyAmount: number;
          if (granularity === 'weekly') {
            // Weekly budgets: use the amount directly (it's already per week)
            weeklyAmount = Math.abs(budgetData.budget_to_base);
          } else {
            // Monthly/yearly budgets: divide evenly across weeks in that period
            weeklyAmount = Math.abs(budgetData.budget_to_base) / weeks.length;
          }

          // Create expected transactions for each week
          // Store amount as positive - the calculation logic will handle categorization
          weeks.forEach(weekStart => {
            const weekDateStr = formatDate(weekStart);
            expectedTransactions.push({
              date: weekDateStr,
              amount: weeklyAmount,
              category_id: budget.category_id,
              categoryType,
              payee: budget.category_name,
              source: 'budget',
              budget_id: budget.category_id,
            });
          });
        } catch (error) {
          console.error(`[Budget] Error processing period ${periodDateStr} for budget ${budget.category_name}:`, error);
        }
      });
    } catch (error) {
      console.error(`[Budget] Error processing budget ${budget.category_name}:`, error);
      console.error(`[Budget] Error stack:`, error instanceof Error ? error.stack : 'No stack trace');
    }
  });

  console.log(`[Budget] Created ${expectedTransactions.length} budget transactions from ${budgets.length} budgets`);
  return expectedTransactions;
}
